import React, { useState, useEffect } from "react";
import { X, ArrowRight, Check } from "lucide-react";
import { inquiry } from "../mock";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InquiryModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSent(false);
        setErrors({});
      }, 300);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 15) e.phone = "Enter a valid phone number";
    if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email address";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent, source: "popup" }),
      });
      if (!res.ok) throw new Error("failed");
      setSent(true);
      setForm({ name: "", phone: "", email: "" });
      setConsent(false);
    } catch (err) {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key, label, type) => (
    <div>
      <input
        type={type}
        placeholder={label}
        value={form[key]}
        onChange={(ev) => setForm({ ...form, [key]: ev.target.value })}
        className={`w-full border-b bg-transparent py-2.5 text-[14px] font-light tracking-[0.05em] text-white placeholder-white/45 transition-colors focus:outline-none ${
          errors[key] ? "border-red-400/70" : "border-white/30 focus:border-bronze-light"
        }`}
      />
      {errors[key] && <p className="mt-1.5 text-[10px] tracking-[0.1em] text-red-300">{errors[key]}</p>}
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center px-5 transition-all duration-300 ${
        open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      <div
        className={`absolute inset-0 bg-black/45 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      <div
        className={`relative w-full max-w-md border border-white/15 bg-[#221810]/55 px-8 py-10 shadow-2xl backdrop-blur-xl transition-all duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:px-10 ${
          open ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        <button onClick={onClose} className="absolute right-5 top-5 text-white/60 transition-colors hover:text-white" aria-label="Close">
          <X size={22} />
        </button>

        {sent ? (
          <div className="py-10 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-bronze-light">
              <Check size={28} className="text-bronze-light" />
            </div>
            <h3 className="font-sans text-xl font-light uppercase tracking-[0.25em] text-[#F1E6D7] sm:text-2xl">Thank You</h3>
            <p className="mt-3 text-[13px] font-light leading-relaxed text-white/70">
              Your inquiry has been received. James will personally follow up shortly.
            </p>
            <button onClick={onClose} className="mt-8 font-sans text-[11px] uppercase tracking-[0.3em] text-bronze-light transition-colors hover:text-white">
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-center font-sans text-[11px] uppercase tracking-[0.3em] text-bronze-light">
              {inquiry.kicker}
            </p>
            <h3 className="mt-4 text-center font-sans text-xl font-light uppercase tracking-[0.25em] text-[#F1E6D7] sm:text-2xl">
              {inquiry.title}
            </h3>
            <p className="mb-8 mt-4 whitespace-pre-line text-center text-[13px] font-light leading-relaxed text-white/70">
              {inquiry.subtitle}
            </p>

            <form onSubmit={submit} noValidate className="space-y-6">
              {field("name", "Full Name", "text")}
              {field("phone", "Phone", "tel")}
              {field("email", "Email", "email")}

              <label className="flex cursor-pointer items-start gap-3">
                <span
                  onClick={() => setConsent(!consent)}
                  className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center border transition-colors ${
                    consent ? "border-bronze-light bg-bronze-light" : "border-white/40"
                  }`}
                >
                  {consent && <Check size={12} className="text-ink" />}
                </span>
                <span className="text-[11px] font-light leading-relaxed text-white/55">{inquiry.consent}</span>
              </label>

              {errors.submit && <p className="text-[11px] tracking-[0.1em] text-red-300">{errors.submit}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="group mt-2 flex w-full items-center justify-center gap-3 bg-[#F1E6D7] py-4 font-sans text-[11px] uppercase tracking-[0.3em] text-ink transition-all duration-500 hover:bg-white disabled:opacity-60"
              >
                {submitting ? "Sending..." : inquiry.cta}
                {!submitting && <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
